import { useEffect, useState, useCallback, useRef } from 'react';
import {
  RefreshCw,
  Play,
  CheckCircle,
  AlertTriangle,
  Clock,
  Loader2,
  Database,
  X,
  Activity,
  UploadCloud,
  Calendar,
} from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';
import { FeedHealthPanel } from './FeedHealthPanel';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { requestControlCentreIngestRefresh } from '../../utils/controlCentreIngestRefresh';

// ── Types ────────────────────────────────────────────────────────────────────

type IngestionSource = 'optifleet' | 'qargo' | 'jigsaw' | 'aquarius' | 'supatrak' | 'samsara' | 'palletways' | 'eflex' | 'tip' | 'xero';

/** Per-card Samsara import: telematics, tachograph or fuel & energy, one per click. */
type SamsaraScope = 'telematics' | 'tachograph' | 'fuel-energy';
type SubmittingKind = 'backfill' | 'incremental' | IngestionSource | `samsara:${SamsaraScope}` | null;

/** Max inclusive days for Data Import “Custom date range” (matches API). */
const MAX_CUSTOM_IMPORT_DAYS = 31;

type ImportMenuTarget = {
  source: IngestionSource;
  samsaraScope?: SamsaraScope;
  step: 'choose' | 'custom';
  from: string;
  to: string;
};

function ymdUtc(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function defaultCustomImportRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - 6);
  return { from: ymdUtc(from), to: ymdUtc(to) };
}

function inclusiveDaySpan(from: string, to: string): number {
  const fromMs = Date.parse(`${from}T00:00:00.000Z`);
  const toMs = Date.parse(`${to}T00:00:00.000Z`);
  if (Number.isNaN(fromMs) || Number.isNaN(toMs)) return NaN;
  return Math.floor((toMs - fromMs) / 86_400_000) + 1;
}

interface SourceStatus {
  source: IngestionSource;
  backfillCompleted: boolean;
  backfillCompletedAt: string | null;
  backfillDateRange: { from: string; to: string } | null;
  backfillError: string | null;
  lastImportedTo: string | null;
  lastImportedAt: string | null;
  totalEventsIngested: number;
}

interface IngestionJobProgress {
  phase: string;
  percentComplete: number;
  currentSource?: string;
  sourceIndex?: number;
  totalSources?: number;
  eventsIngested: number;
  currentDateRange?: { from: string; to: string };
  lastProgressAt: string;
}

interface IngestionJob {
  jobId: string;
  type?: 'backfill' | 'incremental';
  sources?: IngestionSource[];
  dateRange?: { from: string; to: string };
  status: 'pending' | 'running' | 'completed' | 'failed';
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  error?: string;
  totalEventsIngested?: number;
  progress?: IngestionJobProgress;
}

function isXeroOAuthReconnectJobError(error: string): boolean {
  return (
    /invalid_grant/i.test(error)
    || /Refresh token has been consumed/i.test(error)
    || /reconnect required/i.test(error)
  );
}

/** Most recent job that included Xero (jobs list is newest-first from the API). */
function latestXeroIngestionJob(jobs: IngestionJob[]): IngestionJob | undefined {
  let latest: IngestionJob | undefined;
  let latestMs = -Infinity;
  for (const job of jobs) {
    if (!job.sources?.includes('xero')) continue;
    const ms = Date.parse(job.createdAt);
    if (!Number.isFinite(ms) || ms <= latestMs) continue;
    latestMs = ms;
    latest = job;
  }
  return latest;
}

interface ReconstructionJob {
  jobId: string;
  organisationId: string;
  dateRange: { from: string; to: string };
  executionMode: 'historical_backfill' | 'projection_only';
  status: 'queued' | 'running' | 'completed' | 'failed';
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  progress?: {
    totalWindows: number;
    processedWindows: number;
    evidenceRecordsProduced: number;
    evidenceRecordsPersisted: number;
    errors: number;
  };
  error?: string;
}

interface QueueDepth {
  visible: number;
  notVisible: number;
  dlq: number;
}

interface ETAInfo {
  estimatedSeconds: number | null;
  estimatedCompletionTime: string | null;
  messagesPerSec: number | null;
  secondsPerMessage: number | null;
}

interface PipelineStatus {
  queues: Record<string, QueueDepth>;
  etas?: Record<string, ETAInfo>;
  fetchedAt: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const SOURCE_LABELS: Record<IngestionSource, string> = {
  optifleet: 'Optifleet',
  qargo: 'Qargo',
  jigsaw: 'Jigsaw',
  aquarius: 'Aquarius',
  supatrak: 'Supatrak',
  samsara: 'Samsara',
  palletways: 'Palletways',
  eflex: 'Eflex Fuel (CSV)',
  tip: 'TIP (ZF Scalar)',
  xero: 'Xero',
};

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'completed':  return 'text-success bg-success/10 border-success/30';
    case 'running':    return 'text-ring bg-ring/10 border-ring/30';
    case 'pending':    return 'text-warning bg-warning/10 border-warning/30';
    case 'failed':     return 'text-destructive bg-destructive/10 border-destructive/30';
    case 'cancelled':  return 'text-muted-foreground bg-muted-foreground/10 border-accent/30';
    default:           return 'text-muted-foreground bg-muted-foreground/10 border-accent/30';
  }
}

/** Avoid JSON.parse on HTML bodies (e.g. SPA index when same-origin ZeeCore paths are not proxied). */
async function parseZeecoreJson<T>(res: Response, label: string): Promise<T | null> {
  const text = await res.text();
  const t = text.trimStart();
  if (t.startsWith('<')) {
    console.error(
      `${label}: expected JSON from ZeeCore but received HTML. ` +
        'Set VITE_ZEECORE_BASE_URL, or proxy /ingestion (and other truth-layer paths) to ZeeCore in dev (see app/vite.config.ts).',
    );
    return null;
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    console.error(`${label}: invalid JSON`, text.slice(0, 240));
    return null;
  }
}

// ── Pipeline stage definitions ────────────────────────────────────────────────

interface PipelineStage {
  label: string;
  queues: string[];  // one or two queue names that belong to this logical stage
}

const PIPELINE_STAGES: PipelineStage[] = [
  { label: 'Ingestion Jobs',   queues: ['ingestion-import-requested'] },
  { label: 'Raw Import',       queues: ['raw-import-requested', 'raw-object-stored'] },
  { label: 'Event Commit',     queues: ['observed-event-committed'] },
  { label: 'Inference',        queues: ['inference-requested', 'inferred-event-committed'] },
  { label: 'Decisions',        queues: ['decision-requested'] },
  { label: 'Graph',            queues: ['graph-derivation-requested'] },
  { label: 'Profitability',    queues: ['profitability-compute-requested'] },
  // Side pipeline, not part of the linear chain above: Palletways detail
  // enrichment, rate-limited against the vendor API rather than internal
  // throughput. Worth its own stage because its queue can grow instead of
  // drain when its producer (auto-triggered detail backfill) outpaces its
  // single-concurrency consumer — see the 26 Aug incident in
  // zeecore/src/ingest/sources/palletways/detailBackfillJob.ts.
  { label: 'Palletways Detail Repair', queues: ['consignment-status-repair-requested'] },
];

function queueLabel(queueName: string): string {
  return queueName
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function stageSummary(stage: PipelineStage, queues: Record<string, QueueDepth>): { waiting: number; inFlight: number; dlq: number } {
  return stage.queues.reduce(
    (acc, q) => {
      const d = queues[q];
      if (!d) return acc;
      return { waiting: acc.waiting + d.visible, inFlight: acc.inFlight + d.notVisible, dlq: acc.dlq + d.dlq };
    },
    { waiting: 0, inFlight: 0, dlq: 0 }
  );
}

// ── Date preset helpers ───────────────────────────────────────────────────────

function toDateStr(d: Date): string {
  return d.toISOString().split('T')[0];
}

const DATE_PRESETS: { label: string; from: () => string; to: () => string }[] = [
  {
    label: '7 Days',
    from: () => { const d = new Date(); d.setDate(d.getDate() - 7); return toDateStr(d); },
    to: () => toDateStr(new Date()),
  },
  {
    label: '1 Month',
    from: () => { const d = new Date(); d.setMonth(d.getMonth() - 1); return toDateStr(d); },
    to: () => toDateStr(new Date()),
  },
  {
    label: 'Last Quarter',
    from: () => {
      const now = new Date();
      const q = Math.floor(now.getMonth() / 3);
      const year = q === 0 ? now.getFullYear() - 1 : now.getFullYear();
      const startMonth = q === 0 ? 9 : (q - 1) * 3;
      return toDateStr(new Date(year, startMonth, 1));
    },
    to: () => {
      const now = new Date();
      const q = Math.floor(now.getMonth() / 3);
      const year = q === 0 ? now.getFullYear() - 1 : now.getFullYear();
      const endMonth = q === 0 ? 11 : q * 3 - 1;
      const end = new Date(year, endMonth + 1, 0);
      return toDateStr(end);
    },
  },
  {
    label: 'Year to Date',
    from: () => toDateStr(new Date(new Date().getFullYear(), 0, 1)),
    to: () => toDateStr(new Date()),
  },
];

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatNumber(n: number): string {
  return n.toLocaleString('en-GB');
}

function formatETA(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || seconds <= 0) {
    return 'unknown';
  }
  
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  
  if (seconds < 3600) {
    const minutes = Math.round(seconds / 60);
    return `${minutes}m`;
  }
  
  const hours = Math.round(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  
  if (hours < 24) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  
  const days = Math.round(hours / 24);
  return `${days}d`;
}

function formatProcessingTime(secondsPerMessage: number | null | undefined): string {
  if (!secondsPerMessage || secondsPerMessage <= 0) return 'unknown';

  if (secondsPerMessage < 1) {
    return `${Math.round(secondsPerMessage * 1000)}ms/msg`;
  }
  if (secondsPerMessage < 60) {
    return `${secondsPerMessage.toFixed(1)}s/msg`;
  }
  return `${(secondsPerMessage / 60).toFixed(1)}m/msg`;
}

function mergeDateRanges(ranges: Array<{ from: string; to: string }>): { from: string; to: string } | null {
  if (ranges.length === 0) return null;

  return ranges.reduce(
    (acc, range) => ({
      from: range.from < acc.from ? range.from : acc.from,
      to: range.to > acc.to ? range.to : acc.to,
    }),
    { ...ranges[0] },
  );
}

function resolveReconstructionDateRange(
  manualFrom: string,
  manualTo: string,
  sources: SourceStatus[],
): { from: string; to: string } | null {
  const inferred = mergeDateRanges(
    sources
      .map((source) => source.backfillDateRange)
      .filter((range): range is { from: string; to: string } => range !== null),
  );

  const from = manualFrom || inferred?.from;
  const to = manualTo || inferred?.to;

  if (!from || !to) return null;
  return { from, to };
}

// ── Component ────────────────────────────────────────────────────────────────

export default function DataImport() {
  // Status state
  const [sources, setSources] = useState<SourceStatus[]>([]);
  const [jobs, setJobs] = useState<IngestionJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pipeline state
  const [pipeline, setPipeline] = useState<PipelineStatus | null>(null);
  const [redrivingQueues, setRedrivingQueues] = useState<Set<string>>(new Set());
  const [redriveError, setRedriveError] = useState<string | null>(null);
  const pipelinePollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Action state
  const [backfillFrom, setBackfillFrom] = useState('');
  const [backfillTo, setBackfillTo] = useState('');
  const [submitting, setSubmitting] = useState<SubmittingKind>(null);
  const [cancelling, setCancelling] = useState<string | null>(null); // jobId being cancelled
  const [actionError, setActionError] = useState<string | null>(null);
  const [importMenu, setImportMenu] = useState<ImportMenuTarget | null>(null);
  const importMenuRef = useRef<HTMLDivElement | null>(null);

  // Re-import state
  const [reprocessResult, setReprocessResult] = useState<{ totalQueued: number; sources: IngestionSource[] } | null>(null);
  const [reprocessing, setReprocessing] = useState(false);

  // Reconstruction state
  const [reconstructionJobs, setReconstructionJobs] = useState<ReconstructionJob[]>([]);
  const [submittingReconstruction, setSubmittingReconstruction] = useState(false);

  // Identity index rebuild state
  const [rebuildIndexing, setRebuildIndexing] = useState(false);
  const [rebuildResult, setRebuildResult] = useState<{ status: string; total_claims: number; written: number; skipped: number; errors?: string[] } | null>(null);

  // Xero OAuth — show Reconnect when disconnected, Re-authorize when needsReauth.
  // The latest job's token error only matters when status couldn't be read.
  const [xeroConnected, setXeroConnected] = useState<boolean | null>(null);
  const [xeroNeedsReauth, setXeroNeedsReauth] = useState(false);
  const [xeroReconnectBusy, setXeroReconnectBusy] = useState(false);

  // Palletways order-detail backfill state
  const [startingDetailBackfill, setStartingDetailBackfill] = useState(false);
  const [detailBackfillStatus, setDetailBackfillStatus] = useState<{
    status: 'idle' | 'running' | 'paused' | 'completed' | 'failed';
    total: number;
    processed: number;
    succeeded: number;
    failed: number;
    stoppedEarly?: boolean;
  } | null>(null);
  const [seedingAvailableServices, setSeedingAvailableServices] = useState(false);
  const [availableServicesStatus, setAvailableServicesStatus] = useState<{
    present: boolean;
    key: string;
    serviceCount?: number;
    message?: string;
  } | null>(null);

  // Polling refs
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconstructionPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const detailBackfillPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevJobsRef = useRef<IngestionJob[]>([]);

  // ── Fetchers ─────────────────────────────────────────────────────────────

  const fetchStatus = useCallback(async () => {
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.ingestionManage.status());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await parseZeecoreJson<{ sources?: SourceStatus[] }>(res, 'Ingestion status');
      if (!data) return;
      setSources(data.sources ?? []);
    } catch (err) {
      console.error('Failed to fetch ingestion status', err);
    }
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.ingestionManage.jobsList());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await parseZeecoreJson<{ jobs?: IngestionJob[] }>(res, 'Ingestion jobs');
      if (!data) return;
      setJobs(data.jobs ?? []);
    } catch (err) {
      console.error('Failed to fetch ingestion jobs', err);
    }
  }, []);

  const fetchPipeline = useCallback(async () => {
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.ingestionManage.pipelineStatus());
      if (!res.ok) return;
      const data = await parseZeecoreJson<PipelineStatus>(res, 'Pipeline status');
      if (!data) return;
      setPipeline(data);
    } catch (err) {
      console.error('Failed to fetch pipeline status', err);
    }
  }, []);

  const startDlqRedrive = useCallback(async (queueName: string) => {
    setRedriveError(null);
    setRedrivingQueues((prev) => new Set(prev).add(queueName));
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.ingestionManage.dlqRedrive(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ queueName }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }
      await fetchPipeline();
    } catch (err) {
      setRedriveError(err instanceof Error ? err.message : 'Failed to start redrive');
      setRedrivingQueues((prev) => {
        const next = new Set(prev);
        next.delete(queueName);
        return next;
      });
    }
  }, [fetchPipeline]);

  // Clear the optimistic "retrying" flag once the polled DLQ count for that
  // queue reaches zero — the move task itself runs asynchronously in SQS.
  useEffect(() => {
    if (!pipeline || redrivingQueues.size === 0) return;
    setRedrivingQueues((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const q of prev) {
        if ((pipeline.queues[q]?.dlq ?? 0) === 0) {
          next.delete(q);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [pipeline]);

  const fetchReconstructionJobs = useCallback(async () => {
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.reconstruction.jobs());
      if (!res.ok) return;
      const data = await parseZeecoreJson<{ jobs?: ReconstructionJob[] }>(res, 'Reconstruction jobs');
      if (!data) return;
      setReconstructionJobs(data.jobs ?? []);
    } catch (err) {
      console.error('Failed to fetch reconstruction jobs', err);
    }
  }, []);

  const fetchXeroStatus = useCallback(async () => {
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.xeroOAuth.status());
      if (!res.ok) {
        setXeroConnected(null);
        setXeroNeedsReauth(false);
        return;
      }
      const data = await parseZeecoreJson<{ connected?: boolean; needsReauth?: boolean }>(
        res,
        'Xero OAuth status',
      );
      if (!data) {
        setXeroConnected(null);
        setXeroNeedsReauth(false);
        return;
      }
      setXeroConnected(data.connected === true);
      setXeroNeedsReauth(data.needsReauth === true);
    } catch (err) {
      console.error('Failed to fetch Xero OAuth status', err);
      setXeroConnected(null);
      setXeroNeedsReauth(false);
    }
  }, []);

  const startXeroReconnect = useCallback(async () => {
    setXeroReconnectBusy(true);
    setActionError(null);
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.xeroOAuth.start(), {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }
      const data = await parseZeecoreJson<{ authorizeUrl?: string }>(res, 'Xero OAuth start');
      if (!data?.authorizeUrl) {
        throw new Error('Xero reconnect did not return an authorize URL');
      }
      window.location.assign(data.authorizeUrl);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to start Xero reconnect');
      setXeroReconnectBusy(false);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchStatus(),
        fetchJobs(),
        fetchPipeline(),
        fetchReconstructionJobs(),
        fetchXeroStatus(),
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [fetchStatus, fetchJobs, fetchPipeline, fetchReconstructionJobs, fetchXeroStatus]);

  // ── Initial load ─────────────────────────────────────────────────────────

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Close per-source import menu on outside click / Escape
  useEffect(() => {
    if (!importMenu) return;
    const onPointerDown = (e: MouseEvent) => {
      if (importMenuRef.current && !importMenuRef.current.contains(e.target as Node)) {
        setImportMenu(null);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setImportMenu(null);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [importMenu]);

  // ── Poll while active jobs exist ─────────────────────────────────────────

  useEffect(() => {
    const hasActive = jobs.some((j) => j.status === 'pending' || j.status === 'running');

    // Detect newly completed jobs → reload status to get updated watermarks
    const prevActive = new Set(
      prevJobsRef.current
        .filter((j) => j.status === 'running' || j.status === 'pending')
        .map((j) => j.jobId),
    );
    const justCompleted = jobs.filter(
      (j) => (j.status === 'completed' || j.status === 'failed') && prevActive.has(j.jobId),
    );
    if (justCompleted.length > 0) {
      fetchStatus();
      // Control Centre keeps in-memory orders; ask it to re-read after successful ingest
      // (notes → Completed can still lag — FleetDataHub settleFetch covers that).
      const succeeded = justCompleted.filter((j) => j.status === 'completed');
      if (succeeded.length > 0) {
        const hasBackfill = succeeded.some((j) => j.type === 'backfill');
        requestControlCentreIngestRefresh({
          reason: hasBackfill ? 'backfill' : 'incremental',
          jobIds: succeeded.map((j) => j.jobId),
        });
      }
    }
    prevJobsRef.current = jobs;

    if (hasActive && !pollRef.current) {
      pollRef.current = setInterval(fetchJobs, 3_000);
    } else if (!hasActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [jobs, fetchJobs, fetchStatus]);

  // ── Pipeline polling — fast while work is in-flight, slow when idle ───────

  useEffect(() => {
    const totalInFlight = pipeline
      ? Object.values(pipeline.queues).reduce((s, q) => s + q.visible + q.notVisible, 0)
      : 0;
    const interval = totalInFlight > 0 ? 5_000 : 30_000;

    if (pipelinePollRef.current) clearInterval(pipelinePollRef.current);
    pipelinePollRef.current = setInterval(fetchPipeline, interval);

    return () => {
      if (pipelinePollRef.current) {
        clearInterval(pipelinePollRef.current);
        pipelinePollRef.current = null;
      }
    };
  }, [pipeline, fetchPipeline]);

  useEffect(() => {
    const hasActive = reconstructionJobs.some((j) => j.status === 'queued' || j.status === 'running');

    if (hasActive && !reconstructionPollRef.current) {
      reconstructionPollRef.current = setInterval(fetchReconstructionJobs, 3_000);
    } else if (!hasActive && reconstructionPollRef.current) {
      clearInterval(reconstructionPollRef.current);
      reconstructionPollRef.current = null;
    }

    return () => {
      if (reconstructionPollRef.current) {
        clearInterval(reconstructionPollRef.current);
        reconstructionPollRef.current = null;
      }
    };
  }, [reconstructionJobs, fetchReconstructionJobs]);

  const fetchDetailBackfillStatus = useCallback(async () => {
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.palletwaysDetailBackfillStatus());
      if (!res.ok) return;
      const data = await res.json();
      setDetailBackfillStatus(data);
    } catch {
      // transient — next poll tick will retry
    }
  }, []);

  useEffect(() => {
    const running = detailBackfillStatus?.status === 'running' || detailBackfillStatus?.status === 'paused';

    if (running && !detailBackfillPollRef.current) {
      detailBackfillPollRef.current = setInterval(fetchDetailBackfillStatus, 3_000);
    } else if (!running && detailBackfillPollRef.current) {
      clearInterval(detailBackfillPollRef.current);
      detailBackfillPollRef.current = null;
    }

    return () => {
      if (detailBackfillPollRef.current) {
        clearInterval(detailBackfillPollRef.current);
        detailBackfillPollRef.current = null;
      }
    };
  }, [detailBackfillStatus, fetchDetailBackfillStatus]);

  // ── Actions ──────────────────────────────────────────────────────────────

  const startBackfill = async () => {
    setSubmitting('backfill');
    setActionError(null);
    try {
      const body: Record<string, unknown> = {};
      // Only include dateRange if at least one date was specified
      if (backfillFrom || backfillTo) {
        const dr: Record<string, string> = {};
        if (backfillFrom) dr.from = backfillFrom;
        if (backfillTo) dr.to = backfillTo;
        body.dateRange = dr;
      }

      // If backfill is marked complete but has no watermarks, force re-run to initialize watermarks
      if (allBackfillDone && !anyWatermarks) {
        body.force = true;
      }

      const res = await authenticatedFetch(API_ENDPOINTS.ingestionManage.backfill(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (res.status === 409) {
        const data = await res.json().catch(() => ({})) as { message?: string };
        setActionError(data.message ?? 'Backfill already completed. Use force override if needed.');
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { error?: string; message?: string };
        throw new Error(data.message ?? data.error ?? `HTTP ${res.status}`);
      }

      // success — refresh
      await fetchAll();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to start backfill');
    } finally {
      setSubmitting(null);
    }
  };

  const startIncremental = async () => {
    setSubmitting('incremental');
    setActionError(null);
    try {
      // Data Import Incremental is an ops catch-up — use deep refresh (7d lookbacks,
      // enrichment, Aquarius maintenance). Control Centre Refresh All stays routine.
      const res = await authenticatedFetch(API_ENDPOINTS.ingestionManage.incremental(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deepRefresh: true }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { message?: string };
        throw new Error(data.message ?? `HTTP ${res.status}`);
      }

      await fetchAll();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to start incremental import');
    } finally {
      setSubmitting(null);
    }
  };

  /**
   * Per-source Import: same incremental orchestrator as Import Latest, scoped to
   * one enabled source (fleet masters / TIP assets pre-sync + that source's
   * dated feeds). Routine lookbacks — deep catch-up stays on Import Latest.
   *
   * Samsara requires an explicit stream — telematics (GPS / engine / faults) or
   * tachograph (drivers + EU activity) — so ops never pull both in one click.
   *
   * Optional `dateRange` (YYYY-MM-DD) re-pulls that inclusive window exactly
   * (max 31 days). Omit for “since last import”.
   */
  const startSourceImport = async (
    source: IngestionSource,
    samsaraScope?: SamsaraScope,
    dateRange?: { from: string; to: string },
  ) => {
    if (source === 'eflex') return;
    if (source === 'samsara' && !samsaraScope) {
      setActionError('Choose Telematics, Tachograph or Fuel for Samsara.');
      return;
    }

    if (dateRange) {
      const days = inclusiveDaySpan(dateRange.from, dateRange.to);
      if (!dateRange.from || !dateRange.to || Number.isNaN(days)) {
        setActionError('Choose a valid from / to date.');
        return;
      }
      if (dateRange.from > dateRange.to) {
        setActionError('From date must be on or before to date.');
        return;
      }
      if (days > MAX_CUSTOM_IMPORT_DAYS) {
        setActionError(`Custom date range may not exceed ${MAX_CUSTOM_IMPORT_DAYS} days.`);
        return;
      }
    }

    setImportMenu(null);
    setSubmitting(source === 'samsara' && samsaraScope ? `samsara:${samsaraScope}` : source);
    setActionError(null);
    try {
      const body: Record<string, unknown> = { sources: [source] };
      if (source === 'samsara' && samsaraScope) {
        body.samsaraScope = samsaraScope;
      }
      if (dateRange) {
        body.dateRange = dateRange;
      }

      const res = await authenticatedFetch(API_ENDPOINTS.ingestionManage.incremental(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { message?: string; error?: string };
        throw new Error(data.message ?? data.error ?? `HTTP ${res.status}`);
      }

      await fetchAll();
    } catch (err) {
      setActionError(
        err instanceof Error
          ? err.message
          : `Failed to start import for ${SOURCE_LABELS[source]}`,
      );
    } finally {
      setSubmitting(null);
    }
  };

  const openImportMenu = (source: IngestionSource, samsaraScope?: SamsaraScope) => {
    if (source === 'eflex') return;
    // TIP is snapshot-only — no dated window to choose.
    if (source === 'tip') {
      void startSourceImport(source);
      return;
    }
    const defaults = defaultCustomImportRange();
    setImportMenu({
      source,
      samsaraScope,
      step: 'choose',
      from: defaults.from,
      to: defaults.to,
    });
    setActionError(null);
  };

  const isImportMenuOpen = (source: IngestionSource, samsaraScope?: SamsaraScope) =>
    importMenu != null
    && importMenu.source === source
    && (source !== 'samsara' || importMenu.samsaraScope === samsaraScope);

  const renderImportMenu = (source: IngestionSource, samsaraScope?: SamsaraScope) => {
    if (!isImportMenuOpen(source, samsaraScope) || !importMenu) return null;
    const customDays = inclusiveDaySpan(importMenu.from, importMenu.to);
    const customInvalid =
      !importMenu.from
      || !importMenu.to
      || Number.isNaN(customDays)
      || importMenu.from > importMenu.to
      || customDays > MAX_CUSTOM_IMPORT_DAYS;

    return (
      <div
        ref={importMenuRef}
        className={`absolute z-20 top-full mt-1 w-64 min-w-64 rounded-md border border-accent bg-popover shadow-lg p-3 space-y-2.5 ${
          samsaraScope === 'tachograph' || samsaraScope === 'fuel-energy' ? 'right-0' : 'left-0'
        }`}
        role="menu"
      >
        {importMenu.step === 'choose' ? (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              role="menuitem"
              onClick={() => void startSourceImport(source, samsaraScope)}
              className="h-auto w-full rounded px-2.5 py-2 text-left text-sm text-foreground hover:bg-secondary"
            >
              Since last import
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              role="menuitem"
              onClick={() => setImportMenu({ ...importMenu, step: 'custom' })}
              className="h-auto w-full rounded px-2.5 py-2 text-left text-sm text-foreground hover:bg-secondary"
            >
              <Calendar className="w-4 h-4 text-muted-foreground" />
              Custom date range…
            </Button>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">Re-pull window (max {MAX_CUSTOM_IMPORT_DAYS}d)</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setImportMenu({ ...importMenu, step: 'choose' })}
                className="h-auto px-0 text-xs hover:bg-transparent"
              >
                Back
              </Button>
            </div>
            <div className="flex flex-col gap-2">
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                From
                <input
                  type="date"
                  value={importMenu.from}
                  onChange={(e) => setImportMenu({ ...importMenu, from: e.target.value })}
                  className="w-full min-w-0 box-border px-2 py-1.5 rounded bg-card border border-accent text-foreground text-sm"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                To
                <input
                  type="date"
                  value={importMenu.to}
                  onChange={(e) => setImportMenu({ ...importMenu, to: e.target.value })}
                  className="w-full min-w-0 box-border px-2 py-1.5 rounded bg-card border border-accent text-foreground text-sm"
                />
              </label>
            </div>
            {!Number.isNaN(customDays) && customDays > MAX_CUSTOM_IMPORT_DAYS && (
              <p className="text-xs text-warning">
                Range is {customDays} days — shorten to {MAX_CUSTOM_IMPORT_DAYS} or fewer.
              </p>
            )}
            <p className="text-xs text-muted-foreground leading-snug">
              Re-pulls this window (deduped as usual). Does not move the “last import” watermark backwards.
            </p>
            <Button
              type="button"
              variant="default"
              size="sm"
              disabled={customInvalid}
              onClick={() => void startSourceImport(source, samsaraScope, {
                from: importMenu.from,
                to: importMenu.to,
              })}
              className="h-auto w-full rounded bg-primary/80 px-2 py-2 text-sm hover:bg-primary disabled:opacity-40"
            >
              <Play className="w-3.5 h-3.5" />
              Import range
            </Button>
          </>
        )}
      </div>
    );
  };

  const startReconstruction = async () => {
    setSubmittingReconstruction(true);
    setActionError(null);
    try {
      const dateRange = resolveReconstructionDateRange(backfillFrom, backfillTo, sources);
      if (!dateRange) {
        throw new Error('No reconstruction date range is available yet.');
      }

      const res = await authenticatedFetch(API_ENDPOINTS.reconstruction.trigger(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dateRange }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }

      await fetchReconstructionJobs();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to start reconstruction');
    } finally {
      setSubmittingReconstruction(false);
    }
  };

  const startReprocess = async () => {
    setReprocessing(true);
    setReprocessResult(null);
    setActionError(null);
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.ingestionManage.reprocess(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }
      const data = await res.json() as { totalQueued: number; sources: IngestionSource[] };
      setReprocessResult({ totalQueued: data.totalQueued, sources: data.sources });
      await fetchPipeline();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to start reprocess');
    } finally {
      setReprocessing(false);
    }
  };

  const startRebuildIndex = async () => {
    setRebuildIndexing(true);
    setRebuildResult(null);
    setActionError(null);
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.ingestionManage.identityIndexRebuild(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }
      const data = await res.json() as { status: string; total_claims: number; written: number; skipped: number; errors?: string[] };
      setRebuildResult(data);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to rebuild identity index');
    } finally {
      setRebuildIndexing(false);
    }
  };

  const startDetailBackfill = async () => {
    setStartingDetailBackfill(true);
    setActionError(null);
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.palletwaysDetailBackfillStart(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }
      const data = await res.json() as { started: boolean; total: number };
      setDetailBackfillStatus({
        status: data.total > 0 ? 'running' : 'completed',
        total: data.total,
        processed: 0,
        succeeded: 0,
        failed: 0,
      });
      await fetchDetailBackfillStatus();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to start order detail backfill');
    } finally {
      setStartingDetailBackfill(false);
    }
  };

  const fetchAvailableServicesStatus = useCallback(async () => {
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.palletwaysAvailableServicesStatus());
      if (!res.ok) return;
      const data = await res.json() as { present: boolean; key: string; serviceCount?: number };
      setAvailableServicesStatus({
        present: data.present,
        key: data.key,
        serviceCount: data.serviceCount,
      });
    } catch {
      // transient
    }
  }, []);

  useEffect(() => {
    void fetchAvailableServicesStatus();
  }, [fetchAvailableServicesStatus]);

  const seedAvailableServices = async (force = false) => {
    setSeedingAvailableServices(true);
    setActionError(null);
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.palletwaysAvailableServicesSeed(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }
      const data = await res.json() as {
        seeded: boolean;
        alreadyPresent: boolean;
        key: string;
        serviceCount: number;
      };
      setAvailableServicesStatus({
        present: true,
        key: data.key,
        serviceCount: data.serviceCount,
        message: data.seeded
          ? `Seeded ${data.serviceCount.toLocaleString('en-GB')} services`
          : `Already present (${data.serviceCount.toLocaleString('en-GB')} services)`,
      });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to seed AvailableServices catalogue');
    } finally {
      setSeedingAvailableServices(false);
    }
  };

  const cancelJob = async (jobId: string) => {
    setCancelling(jobId);
    setActionError(null);
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.ingestionManage.cancelJob(jobId), {
        method: 'POST',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }
      await fetchJobs();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to cancel job');
    } finally {
      setCancelling(null);
    }
  };

  // ── Derived state ────────────────────────────────────────────────────────

  const allBackfillDone = sources.length > 0 && sources.every((s) => s.backfillCompleted);
  const anyWatermarks = sources.some((s) => s.lastImportedTo !== null);
  const hasActiveJob = jobs.some((j) => j.status === 'pending' || j.status === 'running');
  const reconstructionDateRange = resolveReconstructionDateRange(backfillFrom, backfillTo, sources);
  const hasActiveReconstructionJob = reconstructionJobs.some(
    (j) => j.status === 'queued' || j.status === 'running',
  );

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading && sources.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Loading ingestion status…
      </div>
    );
  }

  if (error && sources.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-destructive">
        <AlertTriangle className="w-5 h-5 mr-2" />
        {error}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Database className="w-5 h-5" />
            Data Import
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage ingestion for the API sources configured for your organisation
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          loading={loading}
          onClick={fetchAll}
          className="h-auto rounded px-3 py-1.5 text-sm"
        >
          Refresh
        </Button>
      </div>

      {/* ── Source Status Cards ──────────────────────────────────────────── */}
      <section>
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
          Source Status
        </h3>
        <p className="text-xs text-muted-foreground mb-3">
          Use Import on a card to pull that source only (fleet masters such as TIP assets /
          Aquarius vehicles, plus that source&apos;s dated feeds). After Import you can choose
          Since last import or a Custom date range (max {MAX_CUSTOM_IMPORT_DAYS} days). Samsara is
          split into Telematics (GPS / engine / faults), Tachograph (driver hours) and Fuel (daily
          fuel and energy reports, whole UK days only). Pick one, then the same window options. Import Latest below still runs every enabled source with
          a deep catch-up.
        </p>
        {sources.length === 0 ? (
          <p className="text-sm text-muted-foreground bg-card border border-border rounded-lg p-4">
            No ingestion sources are enabled for this organisation. Contact your administrator
            to configure API integrations.
          </p>
        ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {sources.map((s) => {
            const csvOnly = s.source === 'eflex';
            const isSamsara = s.source === 'samsara';
            const telematicsBusy = submitting === 'samsara:telematics';
            const tachographBusy = submitting === 'samsara:tachograph';
            const fuelBusy = submitting === 'samsara:fuel-energy';
            const sourceBusy = isSamsara
              ? telematicsBusy || tachographBusy || fuelBusy
              : submitting === s.source;
            // tip / xero may have no watermark yet — still allow Import (first pull).
            // Other sources need a watermark somewhere (or their own) so dated windows exist.
            const importAllowed =
              !csvOnly
              && !hasActiveJob
              && submitting === null
              && (
                s.source === 'tip'
                || s.source === 'xero'
                || s.lastImportedTo !== null
                || anyWatermarks
              );
            const latestXeroJob = latestXeroIngestionJob(jobs);
            const latestXeroJobNeedsReconnect =
              latestXeroJob?.status === 'failed'
              && typeof latestXeroJob.error === 'string'
              && isXeroOAuthReconnectJobError(latestXeroJob.error);
            const xeroOAuthHealthy = xeroConnected === true && !xeroNeedsReauth;
            const showXeroReconnect =
              s.source === 'xero'
              && (
                xeroConnected === false
                || xeroNeedsReauth
                || (!xeroOAuthHealthy && latestXeroJobNeedsReconnect)
              );

            return (
            <div
              key={s.source}
              className="bg-card rounded-lg border border-border p-4 space-y-2"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-white text-sm">
                  {SOURCE_LABELS[s.source]}
                </span>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {s.source === 'xero' && xeroConnected === true && (
                    <Badge tone="success">
                      Connected
                    </Badge>
                  )}
                  {s.backfillCompleted ? (
                    <CheckCircle className="w-4 h-4 text-success" />
                  ) : s.backfillError ? (
                    <AlertTriangle className="w-4 h-4 text-destructive" />
                  ) : (
                    <Clock className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>
              </div>

              <div className="text-xs text-muted-foreground space-y-1">
                <div className="flex justify-between">
                  <span>Backfill</span>
                  <span className={s.backfillCompleted ? 'text-success' : s.backfillError ? 'text-destructive' : 'text-muted-foreground'}>
                    {s.backfillCompleted ? 'Done' : s.backfillError ? 'Error' : 'Pending'}
                  </span>
                </div>
                {s.backfillError && (
                  <div className="text-destructive text-xs mt-1 break-words" title={s.backfillError}>
                    {s.backfillError.length > 60 ? s.backfillError.slice(0, 60) + '…' : s.backfillError}
                  </div>
                )}
                {s.backfillDateRange && (
                  <div className="flex justify-between">
                    <span>Range</span>
                    <span className="text-foreground">
                      {s.backfillDateRange.from} → {s.backfillDateRange.to}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Last Import</span>
                  <span className="text-foreground">{formatDate(s.lastImportedAt)}</span>
                </div>
                <div className="flex justify-between">
                  <span title={s.source === 'tip' ? 'Assets queued from TIP snapshot imports' : 'Events ingested across imports'}>
                    {s.source === 'tip' ? 'Assets' : 'Events'}
                  </span>
                  <span className="text-foreground">{formatNumber(s.totalEventsIngested)}</span>
                </div>
              </div>

              {isSamsara ? (
                <div className="mt-1 grid grid-cols-3 gap-1.5">
                  <div className="relative">
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      loading={telematicsBusy}
                      onClick={() => (
                        isImportMenuOpen('samsara', 'telematics')
                          ? setImportMenu(null)
                          : openImportMenu('samsara', 'telematics')
                      )}
                      disabled={!importAllowed || sourceBusy}
                      title={
                        importAllowed
                          ? 'Import Samsara telematics — since last import or a custom date range'
                          : 'Run backfill first (or Import Latest once watermarks exist)'
                      }
                      className="h-auto w-full rounded bg-primary/80 px-2 py-1.5 text-xs hover:bg-primary disabled:opacity-40"
                    >
                      <Play className="w-3.5 h-3.5" />
                      {telematicsBusy ? 'Queuing…' : 'Telematics'}
                    </Button>
                    {renderImportMenu('samsara', 'telematics')}
                  </div>
                  <div className="relative">
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      loading={tachographBusy}
                      onClick={() => (
                        isImportMenuOpen('samsara', 'tachograph')
                          ? setImportMenu(null)
                          : openImportMenu('samsara', 'tachograph')
                      )}
                      disabled={!importAllowed || sourceBusy}
                      title={
                        importAllowed
                          ? 'Import Samsara tachograph — since last import or a custom date range'
                          : 'Run backfill first (or Import Latest once watermarks exist)'
                      }
                      className="h-auto w-full rounded bg-primary/80 px-2 py-1.5 text-xs hover:bg-primary disabled:opacity-40"
                    >
                      <Play className="w-3.5 h-3.5" />
                      {tachographBusy ? 'Queuing…' : 'Tachograph'}
                    </Button>
                    {renderImportMenu('samsara', 'tachograph')}
                  </div>
                  <div className="relative">
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      loading={fuelBusy}
                      onClick={() => (
                        isImportMenuOpen('samsara', 'fuel-energy')
                          ? setImportMenu(null)
                          : openImportMenu('samsara', 'fuel-energy')
                      )}
                      disabled={!importAllowed || sourceBusy}
                      title={
                        importAllowed
                          ? 'Import Samsara fuel and energy reports (whole UK days) since last import or a custom date range'
                          : 'Run backfill first (or Import Latest once watermarks exist)'
                      }
                      className="h-auto w-full rounded bg-success/80 px-2 py-1.5 text-xs hover:bg-success disabled:opacity-40"
                    >
                      <Play className="w-3.5 h-3.5" />
                      {fuelBusy ? 'Queuing…' : 'Fuel'}
                    </Button>
                    {renderImportMenu('samsara', 'fuel-energy')}
                  </div>
                </div>
              ) : (
                <div className="relative mt-1 space-y-1.5">
                  <Button
                    type="button"
                    onClick={() => (
                      csvOnly
                        ? undefined
                        : isImportMenuOpen(s.source)
                          ? setImportMenu(null)
                          : openImportMenu(s.source)
                    )}
                    disabled={!importAllowed || sourceBusy}
                    title={
                      csvOnly
                        ? 'Eflex uses CSV upload, not API import'
                        : s.source === 'tip'
                          ? 'Pull TIP / ZF Scalar asset list into identity'
                          : s.source === 'xero'
                            ? 'Import Xero invoices, credit notes + P&L for the chosen dates (must match the Profit period chip)'
                          : importAllowed
                            ? `Import ${SOURCE_LABELS[s.source]} — since last import or a custom date range`
                            : 'Run backfill first (or Import Latest once watermarks exist)'
                    }
                    variant="default"
                    size="sm"
                    loading={sourceBusy}
                    className="h-auto w-full rounded bg-primary/80 px-2 py-1.5 text-xs hover:bg-primary disabled:opacity-40"
                  >
                    <Play className="w-3.5 h-3.5" />
                    {csvOnly ? 'CSV only' : sourceBusy ? 'Queuing…' : 'Import'}
                  </Button>
                  {renderImportMenu(s.source)}
                  {showXeroReconnect && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      loading={xeroReconnectBusy}
                      onClick={() => void startXeroReconnect()}
                      disabled={xeroReconnectBusy || hasActiveJob}
                      title={
                        xeroNeedsReauth
                          ? 'Xero rejected the stored refresh token — re-authorize OAuth for this organisation'
                          : xeroConnected === false
                            ? 'Xero is not connected for this organisation — connect OAuth'
                            : 'Re-authorize Xero — the latest import failed with an invalid refresh token'
                      }
                      className="h-auto w-full rounded border border-warning/50 bg-warning/30 px-2 py-1.5 text-xs text-warning hover:bg-warning/40 disabled:opacity-40"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      {xeroReconnectBusy
                        ? 'Opening…'
                        : xeroConnected === false && !xeroNeedsReauth
                          ? 'Reconnect'
                          : 'Re-authorize'}
                    </Button>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>
        )}
      </section>

      <FeedHealthPanel />

      {/* ── Actions ─────────────────────────────────────────────────────── */}
      <section className="bg-card rounded-lg border border-border p-5 space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Actions
        </h3>

        {actionError && (
          <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded px-3 py-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {actionError}
          </div>
        )}

        {/* Backfill */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-white">Backfill</span>
              <p className="text-xs text-muted-foreground">
                {allBackfillDone && anyWatermarks
                  ? 'All sources have been backfilled.'
                  : allBackfillDone && !anyWatermarks
                  ? 'Backfill marked complete but watermarks missing. Click to reinitialize.'
                  : 'Import all historical data. Run once to populate initial data.'}
              </p>
            </div>
            <Button
              variant="default"
              size="sm"
              loading={submitting === 'backfill'}
              onClick={startBackfill}
              disabled={(allBackfillDone && anyWatermarks) || hasActiveJob || submitting !== null}
              className="h-auto rounded bg-ring px-4 py-2 text-sm hover:bg-ring disabled:opacity-40"
            >
              <Play className="w-4 h-4" />
              {allBackfillDone && anyWatermarks ? 'Completed' : allBackfillDone && !anyWatermarks ? 'Reinitialize' : 'Start Backfill'}
            </Button>
          </div>

          {/* Optional date overrides */}
          {!allBackfillDone && (
            <div className="space-y-2">
              {/* Presets */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-muted-foreground">Quick select:</span>
                {DATE_PRESETS.map((p) => (
                  <Button
                    key={p.label}
                    variant="ghost"
                    size="sm"
                    onClick={() => { setBackfillFrom(p.from()); setBackfillTo(p.to()); }}
                    className="h-auto rounded border border-accent px-2 py-0.5 text-xs text-muted-foreground hover:border-ring hover:bg-transparent hover:text-ring"
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
              {/* Manual inputs */}
              <div className="flex items-center gap-3 text-sm">
                <label className="text-muted-foreground">
                  From
                  <input
                    type="date"
                    value={backfillFrom}
                    onChange={(e) => setBackfillFrom(e.target.value)}
                    className="ml-2 px-2 py-1 rounded bg-secondary border border-accent text-foreground text-xs"
                  />
                </label>
                <label className="text-muted-foreground">
                  To
                  <input
                    type="date"
                    value={backfillTo}
                    onChange={(e) => setBackfillTo(e.target.value)}
                    className="ml-2 px-2 py-1 rounded bg-secondary border border-accent text-foreground text-xs"
                  />
                </label>
                <span className="text-xs text-muted-foreground">Leave blank for auto-detect</span>
              </div>
            </div>
          )}
        </div>

        <hr className="border-border" />

        {/* Incremental */}
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm font-medium text-white">Incremental Import</span>
            <p className="text-xs text-muted-foreground">
              Import data since the last import.
              {!anyWatermarks && ' Run backfill first to establish watermarks.'}
            </p>
          </div>
          <Button
            variant="default"
            size="sm"
            loading={submitting === 'incremental'}
            onClick={startIncremental}
            disabled={!anyWatermarks || hasActiveJob || submitting !== null}
            className="h-auto rounded px-4 py-2 text-sm disabled:opacity-40"
          >
            <RefreshCw className="w-4 h-4" />
            Import Latest
          </Button>
        </div>

        <hr className="border-border" />

        <hr className="border-border" />

        {/* Re-import from S3 */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-white">Re-import from S3</span>
              <p className="text-xs text-muted-foreground">
                Re-queue all raw payloads already stored in S3 back through the processing pipeline.
                Safe to run — event idempotency prevents duplicate records.
              </p>
            </div>
            <Button
              variant="warning"
              size="sm"
              loading={reprocessing}
              onClick={startReprocess}
              disabled={reprocessing || hasActiveJob}
              className="h-auto rounded px-4 py-2 text-sm text-white disabled:opacity-40"
            >
              <UploadCloud className="w-4 h-4" />
              Re-import
            </Button>
          </div>
          {reprocessResult && (
            <div className="flex items-center gap-2 text-xs text-success bg-success/10 border border-success/30 rounded px-3 py-2">
              <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
              {reprocessResult.totalQueued.toLocaleString('en-GB')} raw objects queued for reprocessing
              {' '}across {reprocessResult.sources.join(', ')}
            </div>
          )}
        </div>

        <hr className="border-border" />

        {/* Rebuild Identity Index */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-white">Rebuild Identity Index</span>
              <p className="text-xs text-muted-foreground">
                Backfill the entity lookup index from existing identity claims.
                Use after importing historical data to make identifiers searchable.
              </p>
            </div>
            <Button
              variant="default"
              size="sm"
              loading={rebuildIndexing}
              onClick={startRebuildIndex}
              disabled={rebuildIndexing || hasActiveJob}
              className="h-auto rounded bg-chart-5 px-4 py-2 text-sm text-white hover:bg-chart-5/90 disabled:opacity-40"
            >
              <Database className="w-4 h-4" />
              Rebuild Index
            </Button>
          </div>
          {rebuildResult && (
            <div className="flex items-center gap-2 text-xs text-ring bg-ring/10 border border-ring/30 rounded px-3 py-2">
              <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
              Identity index rebuilt: {rebuildResult.written.toLocaleString('en-GB')} entries written
              {rebuildResult.skipped > 0 && `, ${rebuildResult.skipped.toLocaleString('en-GB')} skipped`}
              {rebuildResult.errors && rebuildResult.errors.length > 0 && `, ${rebuildResult.errors.length.toLocaleString('en-GB')} errors`}
            </div>
          )}
        </div>

        <hr className="border-border" />

        {/* Palletways order-detail backfill */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-white">Backfill Palletways Order Details</span>
              <p className="text-xs text-muted-foreground">
                Fetch origin, destination, customer, and weight for consignments that only have the
                base import (no getconsignment detail yet). Runs server-side, paced to stay under
                Palletways' rate limits — safe to leave running.
              </p>
            </div>
            <Button
              onClick={startDetailBackfill}
              disabled={
                startingDetailBackfill
                || detailBackfillStatus?.status === 'running'
                || detailBackfillStatus?.status === 'paused'
              }
              variant="default"
              size="sm"
              loading={
                startingDetailBackfill
                || detailBackfillStatus?.status === 'running'
                || detailBackfillStatus?.status === 'paused'
              }
              className="h-auto rounded bg-chart-10 px-4 py-2 text-sm text-white hover:bg-chart-10/90 disabled:opacity-40"
            >
              <Activity className="w-4 h-4" />
              {detailBackfillStatus?.status === 'paused'
                ? 'Paused…'
                : detailBackfillStatus?.status === 'running'
                  ? 'Running…'
                  : 'Backfill Details'}
            </Button>
          </div>
          {detailBackfillStatus && detailBackfillStatus.total > 0 && (
            <div
              className={`flex items-center gap-2 text-xs rounded px-3 py-2 border ${
                detailBackfillStatus.status === 'completed'
                  ? 'text-success bg-success/10 border-success/30'
                  : detailBackfillStatus.status === 'paused'
                    ? 'text-warning bg-warning/10 border-warning/30'
                    : 'text-chart-10 bg-chart-10/10 border-chart-10/30'
              }`}
            >
              {detailBackfillStatus.status === 'completed' ? (
                <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
              ) : (
                <Loader2 className="w-3.5 h-3.5 flex-shrink-0 animate-spin" />
              )}
              {detailBackfillStatus.processed.toLocaleString('en-GB')} / {detailBackfillStatus.total.toLocaleString('en-GB')} processed
              {' '}({detailBackfillStatus.succeeded.toLocaleString('en-GB')} succeeded
              {detailBackfillStatus.failed > 0 && `, ${detailBackfillStatus.failed.toLocaleString('en-GB')} failed`})
              {detailBackfillStatus.status === 'paused' && ' — paused, pipeline busy'}
              {detailBackfillStatus.stoppedEarly && ' — stopped early after repeated failures, re-run later'}
            </div>
          )}
        </div>

        <hr className="border-border" />

        {/* AvailableServices catalogue (once; not part of consignment backfill) */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-4">
            <div>
              <span className="text-sm font-medium text-white">Seed Palletways Available Services</span>
              <p className="text-xs text-muted-foreground">
                One-time account service table for Orders service names. Stored under the org object
                store — not fetched during consignment backfill or Refresh figures.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {availableServicesStatus?.present && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => void seedAvailableServices(true)}
                  disabled={seedingAvailableServices}
                  className="h-auto rounded border border-accent px-3 py-2 text-xs text-foreground hover:bg-secondary disabled:opacity-40"
                >
                  Refresh
                </Button>
              )}
              <Button
                type="button"
                variant="default"
                size="sm"
                loading={seedingAvailableServices}
                onClick={() => void seedAvailableServices(false)}
                disabled={seedingAvailableServices || availableServicesStatus?.present === true}
                className="h-auto rounded bg-ring px-4 py-2 text-sm text-white hover:bg-ring disabled:opacity-40"
              >
                <UploadCloud className="w-4 h-4" />
                {availableServicesStatus?.present ? 'Seeded' : 'Seed Services'}
              </Button>
            </div>
          </div>
          {availableServicesStatus && (
            <div
              className={`flex items-center gap-2 text-xs rounded px-3 py-2 border ${
                availableServicesStatus.present
                  ? 'text-success bg-success/10 border-success/30'
                  : 'text-warning bg-warning/10 border-warning/30'
              }`}
            >
              {availableServicesStatus.present ? (
                <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
              ) : (
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              )}
              {availableServicesStatus.message
                ?? (availableServicesStatus.present
                  ? `Catalogue present${
                    availableServicesStatus.serviceCount != null
                      ? ` (${availableServicesStatus.serviceCount.toLocaleString('en-GB')} services)`
                      : ''
                  }`
                  : 'Catalogue missing — Orders will omit service names until seeded')}
            </div>
          )}
        </div>

        <hr className="border-border" />

        {/* Historical Reconstruction */}
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm font-medium text-white">Reconstruct Historical Truth Evidence</span>
            <p className="text-xs text-muted-foreground">
              Backfill inferred operational truth for closed historical windows, with estimated mileage, fuel, and driver values attached as secondary evidence.
              {!allBackfillDone && ' Complete backfill first.'}
              {allBackfillDone && reconstructionDateRange && ` Range: ${reconstructionDateRange.from} → ${reconstructionDateRange.to}.`}
              {allBackfillDone && !reconstructionDateRange && ' Backfill coverage is not available yet.'}
            </p>
          </div>
          <Button
            variant="default"
            size="sm"
            loading={submittingReconstruction}
            onClick={startReconstruction}
            disabled={!allBackfillDone || !reconstructionDateRange || hasActiveReconstructionJob || submittingReconstruction}
            className="h-auto rounded bg-chart-5 px-4 py-2 text-sm text-white hover:bg-chart-5/90 disabled:opacity-40"
          >
            <Activity className="w-4 h-4" />
            {hasActiveReconstructionJob ? 'In Progress…' : 'Reconstruct'}
          </Button>
        </div>
      </section>

      {/* ── Jobs ────────────────────────────────────────────────────────── */}
      {jobs.length > 0 && (
        <section>
          <div className="mb-3 flex items-center gap-2">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Recent Jobs
            </h3>
            <Badge tone="neutral">
              {jobs.length}
            </Badge>
          </div>
          <div className="max-h-136 space-y-3 overflow-y-auto pr-1">
            {jobs.map((job) => (
              <JobCard
                key={job.jobId}
                job={job}
                cancelling={cancelling === job.jobId}
                onCancel={cancelJob}
              />
            ))}
          </div>
        </section>
      )}

      {/* ── Pipeline Processing ─────────────────────────────────────────── */}
      <section>
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4" />
          Pipeline Processing
        </h3>
        {pipeline ? (
          <PipelinePanel
            pipeline={pipeline}
            redrivingQueues={redrivingQueues}
            redriveError={redriveError}
            onRedrive={startDlqRedrive}
          />
        ) : (
          <div className="text-sm text-muted-foreground bg-card rounded-lg border border-border p-4">
            Pipeline status unavailable
          </div>
        )}
      </section>
    </div>
  );
}

// ── Pipeline Panel ────────────────────────────────────────────────────────────

function PipelinePanel({
  pipeline,
  redrivingQueues,
  redriveError,
  onRedrive,
}: {
  pipeline: PipelineStatus;
  redrivingQueues: Set<string>;
  redriveError: string | null;
  onRedrive: (queueName: string) => void;
}) {
  const totalActivity = Object.values(pipeline.queues).reduce(
    (s, q) => s + q.visible + q.notVisible,
    0
  );
  const totalDlq = Object.values(pipeline.queues).reduce((s, q) => s + q.dlq, 0);
  const dlqQueues = Object.entries(pipeline.queues).filter(([, d]) => d.dlq > 0);

  // Performance stats — aggregated across whatever tracked queues currently have
  // enough throughput data (see ThroughputTracker's confidence gating).
  const etaList = Object.values(pipeline.etas ?? {});
  const trackedRates = etaList.filter((e): e is ETAInfo & { secondsPerMessage: number } => e.secondsPerMessage !== null);
  const avgSecondsPerMessage = trackedRates.length > 0
    ? trackedRates.reduce((s, e) => s + e.secondsPerMessage, 0) / trackedRates.length
    : null;
  const completionSeconds = etaList
    .map((e) => e.estimatedSeconds)
    .filter((s): s is number => s !== null);
  // Pipeline finishes when its slowest tracked stage does.
  const overallEtaSeconds = completionSeconds.length > 0 ? Math.max(...completionSeconds) : null;

  return (
    <div className="bg-card rounded-lg border border-border p-4 space-y-4">
      {/* Summary row */}
      <div className="flex items-center justify-between text-xs text-muted-foreground flex-wrap gap-2">
        <span>
          {totalActivity > 0 ? (
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-ring animate-pulse" />
              <span className="text-ring">{formatNumber(totalActivity)} messages in pipeline</span>
            </span>
          ) : (
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-success" />
              <span className="text-success">All queues idle</span>
            </span>
          )}
        </span>
        {totalDlq > 0 && (
          <span className="flex items-center gap-1 text-warning">
            <AlertTriangle className="w-3.5 h-3.5" />
            {formatNumber(totalDlq)} failed messages in DLQs
          </span>
        )}
      </div>

      {/* Performance stats */}
      {(avgSecondsPerMessage !== null || overallEtaSeconds !== null) && (
        <div className="flex items-center gap-4 text-xs flex-wrap rounded border border-border bg-background/40 px-3 py-2">
          {avgSecondsPerMessage !== null && (
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">Avg. time per message</span>
              <span className="text-chart-10 font-medium">{formatProcessingTime(avgSecondsPerMessage)}</span>
            </span>
          )}
          {overallEtaSeconds !== null && (
            <span className="flex items-center gap-1.5">
              <span className="text-muted-foreground">Est. time to completion</span>
              <span className="text-success font-medium">{formatETA(overallEtaSeconds)}</span>
            </span>
          )}
        </div>
      )}

      {/* Dead Letter Queues */}
      {dlqQueues.length > 0 && (
        <div className="rounded border border-warning/40 bg-warning/5 p-3 space-y-2">
          <div className="flex items-center gap-2 text-xs font-medium text-warning">
            <AlertTriangle className="w-3.5 h-3.5" />
            Dead Letter Queues
          </div>
          <div className="space-y-1.5">
            {dlqQueues.map(([queueName, d]) => {
              const retrying = redrivingQueues.has(queueName);
              return (
                <div key={queueName} className="flex items-center justify-between text-xs">
                  <span className="text-foreground">{queueLabel(queueName)}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-warning font-medium">{formatNumber(d.dlq)} failed</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      loading={retrying}
                      onClick={() => onRedrive(queueName)}
                      className="h-auto rounded border border-warning/40 px-2 py-0.5 text-xs text-warning hover:bg-warning/10 disabled:opacity-40"
                    >
                      {retrying ? 'Retrying…' : 'Retry'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          {redriveError && <p className="text-xs text-destructive">{redriveError}</p>}
        </div>
      )}

      {/* Stage grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {PIPELINE_STAGES.map((stage, i) => {
          const { waiting, inFlight, dlq } = stageSummary(stage, pipeline.queues);
          const total = waiting + inFlight;
          const isActive = total > 0;
          const hasError = dlq > 0;
          
          // Get ETA if available for this stage (map stage names to queue names)
          const etaQueueName = stage.label === 'Decisions' ? 'decision-requested'
            : stage.label === 'Graph' ? 'graph-derivation-requested'
            : stage.label === 'Profitability' ? 'profitability-compute-requested'
            : stage.label === 'Palletways Detail Repair' ? 'consignment-status-repair-requested'
            : null;
          const eta = etaQueueName && pipeline.etas?.[etaQueueName];

          return (
            <div key={stage.label} className="relative flex flex-col">
              {/* Connector arrow (not on last item) */}
              {i < PIPELINE_STAGES.length - 1 && (
                <div className="hidden lg:block absolute -right-2 top-1/2 -translate-y-1/2 z-10 text-accent text-sm">›</div>
              )}
              <div
                className={`rounded border p-3 space-y-1.5 ${
                  hasError
                    ? 'border-warning/40 bg-warning/5'
                    : isActive
                    ? 'border-ring/40 bg-ring/5'
                    : 'border-border'
                }`}
              >
                <div className="flex items-center justify-between gap-1">
                  <span className="text-xs font-medium text-foreground truncate">{stage.label}</span>
                  {isActive && <span className="inline-block w-1.5 h-1.5 rounded-full bg-ring animate-pulse flex-shrink-0" />}
                </div>
                <div className="text-xs space-y-0.5">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Waiting</span>
                    <span className={waiting > 0 ? 'text-ring font-medium' : 'text-accent'}>
                      {formatNumber(waiting)}
                    </span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Active</span>
                    <span className={inFlight > 0 ? 'text-chart-10 font-medium' : 'text-accent'}>
                      {formatNumber(inFlight)}
                    </span>
                  </div>
                  {dlq > 0 && (
                    <div className="flex justify-between">
                      <span className="text-warning">Failed</span>
                      <span className="text-warning font-medium">{formatNumber(dlq)}</span>
                    </div>
                  )}
                  {eta && (eta.secondsPerMessage || eta.estimatedSeconds) && (
                    <div className="pt-1 border-t border-accent/50 space-y-0.5">
                      {eta.secondsPerMessage && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Per msg</span>
                          <span className="text-chart-10 font-medium">
                            {formatProcessingTime(eta.secondsPerMessage)}
                          </span>
                        </div>
                      )}
                      {eta.estimatedSeconds && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">ETA</span>
                          <span className="text-success font-medium">
                            {formatETA(eta.estimatedSeconds)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-accent">
        Last checked {new Date(pipeline.fetchedAt).toLocaleTimeString('en-GB')}
        {' '}· Auto-refreshes {
          Object.values(pipeline.queues).reduce((s, q) => s + q.visible + q.notVisible, 0) > 0
            ? 'every 5s'
            : 'every 30s'
        }
      </p>
    </div>
  );
}

// ── Job Card ─────────────────────────────────────────────────────────────────

function JobCard({
  job,
  cancelling,
  onCancel,
}: {
  job: IngestionJob;
  cancelling: boolean;
  onCancel: (jobId: string) => void;
}) {
  const isActive = job.status === 'pending' || job.status === 'running';

  return (
    <div className="bg-card rounded-lg border border-border p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className={`px-2 py-0.5 rounded border text-xs font-medium ${statusBadgeClass(job.status)}`}>
            {job.status}
          </span>
          <span className="text-sm font-medium text-white capitalize">{job.type}</span>
          {job.dateRange && (
            <span className="text-xs text-muted-foreground">
              {job.dateRange.from} → {job.dateRange.to}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{formatDate(job.createdAt)}</span>
          {isActive && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onCancel(job.jobId)}
              disabled={cancelling}
              title="Cancel job"
              className="h-auto gap-1 rounded border border-destructive/40 px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
            >
              {cancelling ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
              Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Sources */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {(job.sources ?? []).map((s) => (
          <span
            key={s}
            className={`px-1.5 py-0.5 rounded text-xs ${
              job.progress?.currentSource === s
                ? 'bg-ring/20 text-ring border border-ring/30'
                : 'bg-secondary text-muted-foreground'
            }`}
          >
            {SOURCE_LABELS[s] ?? s}
          </span>
        ))}
      </div>

      {/* Progress bar */}
      {job.progress && isActive && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{job.progress.phase}</span>
            <span>{Math.round(job.progress.percentComplete)}%</span>
          </div>
          <div className="w-full bg-secondary rounded-full h-2">
            <div
              className="bg-ring h-2 rounded-full transition-all duration-500"
              style={{ width: `${job.progress.percentComplete}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{formatNumber(job.progress.eventsIngested)} events</span>
            {job.progress.currentDateRange && (
              <span>
                {job.progress.currentDateRange.from} → {job.progress.currentDateRange.to}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Completed stats */}
      {job.status === 'completed' && job.totalEventsIngested != null && (
        <div className="text-xs text-muted-foreground">
          {formatNumber(job.totalEventsIngested)} events ingested
          {job.completedAt && <> • Completed {formatDate(job.completedAt)}</>}
        </div>
      )}

      {/* Error */}
      {job.status === 'failed' && job.error && (
        <div className="text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded px-2 py-1">
          {job.error}
        </div>
      )}
    </div>
  );
}
